import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons/index.jsx';
import { api } from '../../services/api/client.js';
import { formatPostingMoney } from '../../lib/postingPlan.js';

export function validateScheduledPostingQuote(input, expectedEffectiveAt, expectedScheduleId) {
  const quote = input?.data && typeof input.data === 'object' ? input.data : input;
  const integers = ['unitAmount', 'currentQuantity', 'attachedCount', 'newQuantity', 'recurringTotal', 'periodEnd'];
  if (!quote || quote.plan !== 'posting' || quote.estimate !== true) return null;
  if (typeof quote.scheduleId !== 'string' || !quote.scheduleId || typeof quote.subscriptionId !== 'string' || !quote.subscriptionId) return null;
  if (expectedScheduleId && quote.scheduleId !== expectedScheduleId) return null;
  if (typeof quote.currency !== 'string' || !/^[a-z]{3}$/i.test(quote.currency)) return null;
  if (integers.some((field) => !Number.isSafeInteger(quote[field]) || quote[field] < 0)) return null;
  if (quote.currentQuantity < 1 || quote.periodEnd < 1) return null;
  if (quote.newQuantity !== Math.max(1, quote.attachedCount)) return null;
  if (quote.recurringTotal !== quote.unitAmount * quote.newQuantity || !Number.isSafeInteger(quote.recurringTotal)) return null;

  const expectedSeconds = new Date(expectedEffectiveAt).getTime() / 1000;
  if (!Number.isInteger(expectedSeconds) || quote.periodEnd !== expectedSeconds) return null;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: quote.currency.toUpperCase() });
  } catch {
    return null;
  }
  return quote;
}

export function ScheduledPostingChangeCardView({
  currentPlanName,
  effectiveAt,
  quote,
  status,
  onRetry,
  onUndo,
  undoing,
  undoError,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const effectiveDate = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
    .format(new Date(effectiveAt)).replace(/ /g, '\u00a0');
  const total = quote ? formatPostingMoney(quote.recurringTotal, quote.currency, locale) : null;
  const CalendarIcon = I.calendar;

  return (
    <section className="scheduled-posting-change" aria-labelledby="scheduled-posting-title">
      <div className="scheduled-posting-heading">
        <span className="scheduled-posting-icon" aria-hidden="true"><CalendarIcon size={16} /></span>
        <div>
          <div className="scheduled-posting-title" id="scheduled-posting-title">
            {t('settings.downgradeScheduledTitle', { plan: t('postingPlan.name'), date: effectiveDate })}
          </div>
          <p>{t('settings.scheduledCurrentUntil', { currentPlan: currentPlanName })}</p>
        </div>
      </div>

      <div className="scheduled-posting-estimate">
        <div className="scheduled-posting-estimate-label">{t('settings.scheduledPostingEstimateLabel')}</div>
        {status === 'loading' && <div className="scheduled-posting-state" role="status">{t('settings.scheduledPostingEstimateLoading')}</div>}
        {status === 'error' && (
          <div className="scheduled-posting-state" role="alert">
            <span>{t('settings.scheduledPostingEstimateError')}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>{t('settings.retry')}</button>
          </div>
        )}
        {status === 'ready' && quote && (
          <>
            <div className="scheduled-posting-calculation">
              {t('settings.scheduledPostingTotal', { total })}
            </div>
            <div className="scheduled-posting-minimum">{quote.attachedCount === 0
              ? t('settings.scheduledPostingMinimumExplanation')
              : t('settings.scheduledPostingAccounts', { count: quote.attachedCount })}</div>
            <div className="scheduled-posting-explanation">{t('settings.scheduledPostingEstimateExplanation')}</div>
          </>
        )}
      </div>

      {undoError && <div className="scheduled-posting-error" role="alert">{undoError}</div>}
      <button className="btn btn-secondary scheduled-posting-undo" onClick={onUndo} disabled={undoing} aria-busy={undoing} type="button">
        {t('settings.keepCurrentPlan', { currentPlan: currentPlanName })}{undoing ? '…' : ''}
      </button>
    </section>
  );
}

export default function ScheduledPostingChangeCard({
  accountId,
  pendingPlan = 'posting',
  scheduleId,
  effectiveAt,
  currentPlanName,
  onUndo,
  undoing,
  undoError,
}) {
  const [requestVersion, setRequestVersion] = useState(0);
  const identity = useMemo(
    () => [accountId || '', pendingPlan || '', effectiveAt, scheduleId || ''].join('|'),
    [accountId, pendingPlan, effectiveAt, scheduleId],
  );
  const expectedScheduleId = scheduleId || null;
  const [quoteState, setQuoteState] = useState(() => ({ identity, expectedScheduleId, status: 'loading', quote: null }));
  const requestRef = useRef(0);
  const renderedQuoteState = quoteState.identity === identity && quoteState.expectedScheduleId === expectedScheduleId
    ? quoteState
    : { status: 'loading', quote: null };

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (undoing) {
      setQuoteState({ identity, expectedScheduleId, status: 'loading', quote: null });
      return () => { requestRef.current += 1; };
    }
    setQuoteState({ identity, expectedScheduleId, status: 'loading', quote: null });
    api.billing.getScheduledPostingQuote()
      .then((response) => {
        if (requestRef.current !== requestId) return;
        const quote = validateScheduledPostingQuote(response, effectiveAt, scheduleId);
        setQuoteState(quote
          ? { identity, expectedScheduleId, status: 'ready', quote }
          : { identity, expectedScheduleId, status: 'error', quote: null });
      })
      .catch(() => {
        if (requestRef.current === requestId) setQuoteState({ identity, expectedScheduleId, status: 'error', quote: null });
      });
    return () => {
      if (requestRef.current === requestId) requestRef.current += 1;
    };
  }, [identity, requestVersion, undoing, effectiveAt, scheduleId]);

  return (
    <ScheduledPostingChangeCardView
      currentPlanName={currentPlanName}
      effectiveAt={effectiveAt}
      quote={undoing ? null : renderedQuoteState.quote}
      status={undoing ? 'loading' : renderedQuoteState.status}
      onRetry={() => setRequestVersion((version) => version + 1)}
      onUndo={() => onUndo(renderedQuoteState.quote?.scheduleId || null)}
      undoing={undoing}
      undoError={undoError}
    />
  );
}
